import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { setCookie } from 'utilities/browser';
import {
	Drawer, OverlayContent, useOverlayState
} from 'components/Overlay';
import ModelContext from 'features/sort/modelContext';
import useDisableScroll from 'features/sort/useDisableScroll';
import { setDynamicDrawerOpenState } from 'features/sort/dynamicPersistenceStore';

import DrawerDisclosure from '../../DrawerDisclosure';

import FiltersContent from './FiltersContent';

import './SortFilterDisplaySetLeftNav.scss';

const collapsedLeftNavClassState = 'filtersAndProducts--leftNavLayoutCollapsed';
const largeViewportSize = 1280;
const mediaQuery = window.matchMedia(`(min-width: ${largeViewportSize}px)`);
const mediaQueryHideDrawer = window.matchMedia(`(min-width: ${largeViewportSize + 20}px)`);

const SortFilterDisplaySetLeftNav = ({
	attributeGroups = [],
	appliedFilters = [],
	defaultVisibleMenuCount,
	className = ''
}) => {

	const defaultClassName = 'SortFilterDisplaySetLeftNav';
	const filterButtonTriggerClassName = 'SortFilterButtonTrigger';
	const dispatch = useDispatch();
	const isDrawerOpenDynamicState = useSelector((state) => state.ui.isDrawerOpen);
	const drawer = useOverlayState(isDrawerOpenDynamicState, '');
	const { hideOverlay, toggleOverlay, show } = drawer;
	const hasAppliedFilters = appliedFilters && appliedFilters.length > 0;
	const headerClassName = classNames({ [`${defaultClassName}__header--withBorder`]: !hasAppliedFilters });
	const { CanonicalOrderedUrlFragments } = useContext(ModelContext);
	const [ isLargeViewportSize, setIsLargeViewportSize ] = useState(mediaQuery.matches);
	// can only select one checkbox at a time. When one is selected the rest are disabled.
	const [ isNavigating, setIsNavigating ] = useState(false);
	const isLeftNavOnPage = isLargeViewportSize && !show;
	const fullClassName = classNames(defaultClassName, className);

	const filterBtnHandler = () => {

		if (isLargeViewportSize) {

			const layoutContainer = window.document.querySelector('.filtersAndProducts--leftNavLayout');
			layoutContainer?.classList.toggle(collapsedLeftNavClassState);
			setCookie(
				'LeftNavCollapsed',
				layoutContainer?.classList.contains(collapsedLeftNavClassState).toString(),
				null,
				null,
				'/',
				true,
				true
			);

		} else {

			toggleOverlay();

		}

	};

	useEffect(() => {

		// createPortal doesn't replace content of it's container
		[ ...document.querySelectorAll('.skeleton--leftNav') ].forEach((e) => e.remove());

		dispatch(setDynamicDrawerOpenState(show));

		const setViewportState = (event) => {

			setIsLargeViewportSize(event.matches);

		};

		const hideOverlayOnResize = () => {

			if (show) {

				hideOverlay();

			}

		};

		mediaQuery.addEventListener('change', setViewportState);
		// Safari bug - CSS media query is triggered when drawer hides scrollbar and it was auto closing overlay
		// So we only hide the drawer if its +20px past viewport
		mediaQueryHideDrawer.addEventListener('change', hideOverlayOnResize);

		const handleDynamicNavigation = () => {

			setIsNavigating(false);

		};
		document.addEventListener('dynamicNavigationReady', handleDynamicNavigation);

		return () => {

			mediaQuery.removeEventListener('change', setViewportState);
			mediaQueryHideDrawer.removeEventListener('change', hideOverlayOnResize);
			document.removeEventListener('dynamicNavigationReady', handleDynamicNavigation);

		};

	}, [ show ]);

	useDisableScroll(show);

	return (
		<div className={filterButtonTriggerClassName}>
			<DrawerDisclosure
				drawer={drawer}
				onButtonClick={filterBtnHandler}
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="16.333" height="13" viewBox="0 0 16.333 13" aria-hidden="true">
					<g id="Group_5" data-name="Group 5" transform="translate(-305.223 -199)">
						<path id="Path_5" data-name="Path 5" d="M290.445,145.842h16.333" transform="translate(14.777 55)" fill="none" stroke="#333" strokeWidth="1" />
						<path id="Path_6" data-name="Path 6" d="M290.445,145.842h16.333" transform="translate(14.777 59.593)" fill="none" stroke="#333" strokeWidth="1" />
						<path id="Path_7" data-name="Path 7" d="M290.445,145.842h16.333" transform="translate(14.777 64.185)" fill="none" stroke="#333" strokeWidth="1" />
						<g id="Ellipse_1" data-name="Ellipse 1" transform="translate(315.777 199)" fill="#fff" stroke="#333" strokeWidth="1">
							<circle cx="2" cy="2" r="2" stroke="none" />
							<circle cx="2" cy="2" r="1.5" fill="none" />
						</g>
						<g id="Ellipse_2" data-name="Ellipse 2" transform="translate(307.777 203)" fill="#fff" stroke="#333" strokeWidth="1">
							<circle cx="2" cy="2" r="2" stroke="none" />
							<circle cx="2" cy="2" r="1.5" fill="none" />
						</g>
						<g id="Ellipse_3" data-name="Ellipse 3" transform="translate(311.777 208)" fill="#fff" stroke="#333" strokeWidth="1">
							<circle cx="2" cy="2" r="2" stroke="none" />
							<circle cx="2" cy="2" r="1.5" fill="none" />
						</g>
					</g>
				</svg>
				Filter
			</DrawerDisclosure>

			{isLeftNavOnPage ? createPortal(
				<div className={fullClassName}>
					<FiltersContent
						appliedFilters={appliedFilters}
						attributeGroups={attributeGroups}
						canonicalOrderedUrlFragments={CanonicalOrderedUrlFragments}
						isNavigating={isNavigating}
						setIsNavigating={setIsNavigating}
						defaultVisibleMenuCount={defaultVisibleMenuCount}
					/>
				</div>,
				document.querySelector('.setDT-3-leftNav')
			) : (
				<Drawer
					{...drawer}
					className={fullClassName}
					aria-label="Filters"
					anchorEdge="left"
					underlayClickExits
					focusDialog
					scrollDisabled={false}
					escapeExits
					fullScreen={window?.lp?.globals?.isMobile}
					closeButtonVariant="text"
				>
					<OverlayContent>
						<header className={headerClassName}>Filters</header>
						<FiltersContent
							appliedFilters={appliedFilters}
							attributeGroups={attributeGroups}
							canonicalOrderedUrlFragments={CanonicalOrderedUrlFragments}
							isNavigating={isNavigating}
							setIsNavigating={setIsNavigating}
							defaultVisibleMenuCount={defaultVisibleMenuCount}
						/>
					</OverlayContent>
				</Drawer>
			)}
		</div>
	);

};

SortFilterDisplaySetLeftNav.defaults = {};

SortFilterDisplaySetLeftNav.propTypes = {
	attributeGroups: PropTypes.oneOfType([ PropTypes.arrayOf(PropTypes.object) ]).isRequired,
	className: PropTypes.string,
	appliedFilters: PropTypes.oneOfType([
		PropTypes.arrayOf(
			PropTypes.shape({
				Name: PropTypes.string,
				AttributeValues: PropTypes.arrayOf(PropTypes.string),
				StepbackLink: PropTypes.string
			})
		)
	]),
	canonicalOrderedUrlFragments: PropTypes.shape({
		IdBasedList: PropTypes.arrayOf(PropTypes.string),
		TextBasedList: PropTypes.arrayOf(PropTypes.string),
		ShouldUseIdBasedUrl: PropTypes.bool
	}),
	defaultVisibleMenuCount: PropTypes.number
};

export default SortFilterDisplaySetLeftNav;
